import { lazy, Suspense, useCallback, useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, CircularProgress, Container, Typography, Alert } from '@mui/material';
import EmailForm from '../sections/@dashboard/settings/EmailForm';
import { useAuthContext } from '../auth/useAuthContext';
import axios from '../utils/Axios';
import saveErrorMessage from '../utils/saveErrorMessage';

// The new Emails page is a separate chunk, so accounts outside the email pilot never
// download the editor (build plan section 3.0).
const EmailsUpgradePage = lazy(() => import('../sections/@dashboard/emails/EmailsUpgradePage'));

export const inEmailPilot = (user) => user?.emailUpgrade?.pilot === true && user?.emailUpgrade?.blocked !== true;

export const UPGRADE_OFF_AFTER_SAVE =
  'The new email editor is off for your account. Some of your last changes may not be saved. Check your emails below.';

// Pilot accounts get the new page. When the server says the upgrade is off for the account
// (a 403 `email_upgrade_off`, or today's answer from GET or PUT /emails), the page hands
// back to today's page. After a save, the owner is told, because today's save path keeps
// only today's fields.
export default function EmailCustomizerPage() {
  const { user } = useAuthContext();
  const [upgradeOff, setUpgradeOff] = useState(null);
  const handleUpgradeOff = useCallback((reason) => setUpgradeOff({ reason }), []);

  if (!inEmailPilot(user) || upgradeOff) {
    return (
      <>
        {upgradeOff?.reason === 'save' && (
          <Container maxWidth="xl">
            <Alert severity="warning" sx={{ mb: 2 }}>
              {UPGRADE_OFF_AFTER_SAVE}
            </Alert>
          </Container>
        )}
        <LegacyEmailCustomizerPage />
      </>
    );
  }

  return (
    <Suspense
      fallback={
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      }
    >
      <EmailsUpgradePage onUpgradeOff={handleUpgradeOff} />
    </Suspense>
  );
}

// Today's Email Customizer, unchanged.
function LegacyEmailCustomizerPage() {
  const [emails, setEmails] = useState({});
  const [status, setStatus] = useState(null);
  const dismissTimer = useRef(null);

  useEffect(() => () => clearTimeout(dismissTimer.current), []);

  const getEmails = useCallback(async () => {
    try {
      const emailsResponse = await axios.get('/emails');
      setEmails(emailsResponse.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  // A rejected save used to be swallowed here: the owner saw no message, and the form
  // counted the submit as successful, which cleared its unsaved-changes warning for
  // templates that were never saved. The error is now shown and re-raised, so the form
  // can record the failure. The form catches it; see settings/EmailForm.js.
  const updateEmails = useCallback(async (emails) => {
    clearTimeout(dismissTimer.current);
    delete emails._id;
    delete emails.createdAt;
    delete emails.updatedAt;

    // Only the request is inside the try, so a failure after a successful save is not
    // reported as a failed save.
    let emailsResponse;
    try {
      emailsResponse = await axios.put('/emails', { emails });
    } catch (error) {
      console.error('Email Customizer save failed', error);
      setStatus({ severity: 'error', message: saveErrorMessage(error, 'We could not save your email templates. Please try again.') });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      throw error;
    }

    setEmails({ ...emailsResponse.data.emails });
    setStatus({ severity: 'success', message: 'Settings Updated.' });
    dismissTimer.current = setTimeout(() => setStatus(null), 6000);
  }, []);

  useEffect(() => {
    getEmails();
  }, []);

  return (
    <>
      <Helmet>
        <title>Email Customizer | {localStorage.getItem("companyName") ?? "FlowBookings"}</title>
      </Helmet>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 5 }}>
          Email Customizer
        </Typography>
        {status != null && (
          <Alert
            severity={status.severity}
            onClose={status.severity === 'error' ? () => setStatus(null) : undefined}
            sx={{ mb: 2, whiteSpace: 'pre-line' }}
          >
            {status.message}
          </Alert>
        )}
        <EmailForm emails={emails} updateEmails={updateEmails} />
      </Container>
    </>
  );
}
